/**
 * src/Blockamatic.jsx
 *
 * Main app component — fully integrated with:
 *   - useCapture      (JWT-enforced capture flow)
 *   - useLightningPurchase (NWC invoice + polling)
 *   - tokenStore      (IndexedDB token persistence)
 *   - loadInventory   (shot counts from IndexedDB)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useCapture } from "./hooks/useCapture.js";
import { useLightningPurchase } from "./hooks/useLightningPurchase.js";
import { loadInventory } from "./lib/tokenStore.js";
import { getAllPhotos, savePhoto, deletePhoto } from "./lib/photoStore.js";
import { prefetchShutterAudio, primeShutterAudio } from "./lib/shutterSound.js";
import QRCode from "react-qr-code";
import packageJson from "../package.json";

// ─── Film style definitions ───────────────────────────────────────────────────
// Prices + shots are authoritative on the server (api/filmRolls.js).
// These client-side values drive UI only.
const _ROLL_PRICE = (() => {
  const n = parseInt(import.meta.env.FILM_PRICE, 10);
  return Number.isFinite(n) && n > 0 ? n : 2100;
})();

export const FILM_STYLES = {
  standard: {
    id: "standard",
    name: "Standard",
    shots: Infinity,
    price: 0,
    free: true,
    stampColor: "#FF6B00",
    stampGlow: "#FF4500",
    filter: "none",
    grain: 0.12,
    vignette: 0.5,
    description: "The original. Warm amber stamp, light grain.",
    iso: "ISO 400",
  },
  kodachrome: {
    id: "kodachrome",
    name: "Kodachrome",
    shots: 21,
    price: _ROLL_PRICE,
    free: false,
    stampColor: "#FFF5C0",
    stampGlow: "#FFD700",
    filter: "saturate(1.4) contrast(1.1) sepia(0.15)",
    grain: 0.08,
    vignette: 0.4,
    description: "Lifted shadows, punchy saturation, cream stamp glow.",
    iso: "ISO 64",
  },
  trix: {
    id: "trix",
    name: "Tri-X",
    shots: 21,
    price: _ROLL_PRICE,
    free: false,
    stampColor: "#FFFFFF",
    stampGlow: "#AAAAAA",
    filter: "grayscale(1) contrast(1.3) brightness(0.95)",
    grain: 0.22,
    vignette: 0.65,
    description: "High-contrast black & white with heavy grain.",
    iso: "ISO 400",
  },
  cinestill: {
    id: "cinestill",
    name: "Cinestill 800T",
    shots: 21,
    price: _ROLL_PRICE,
    free: false,
    stampColor: "#FF3030",
    stampGlow: "#FF0000",
    filter: "saturate(1.2) hue-rotate(-15deg) contrast(1.05)",
    grain: 0.18,
    vignette: 0.7,
    description: "Teal-orange halation, tungsten grade, red stamp halo.",
    iso: "ISO 800",
  },
  lomochrome: {
    id: "lomochrome",
    name: "Lomochrome",
    shots: 21,
    price: _ROLL_PRICE,
    free: false,
    stampColor: "#E040FB",
    stampGlow: "#9C27B0",
    filter: "saturate(1.3) hue-rotate(60deg) contrast(1.15) brightness(0.9)",
    grain: 0.28,
    vignette: 0.8,
    description: "Color shift (greens → purple), heavy vignette, lo-fi.",
    iso: "ISO 100-400",
  },
};

// ─── Viewfinder overlays ──────────────────────────────────────────────────────

function GrainOverlay({ intensity }) {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3,
      opacity: intensity,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
      backgroundSize: "128px 128px",
      mixBlendMode: "overlay",
    }} />
  );
}

function VignetteOverlay({ strength }) {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4,
      background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${strength}) 100%)`,
    }} />
  );
}

function BrandLogo({ src = "/logo.png", height = 12, opacity = 1, marginBottom = 0 }) {
  return (
    <img
      src={src}
      alt="Blockamatic"
      style={{
        height,
        width: "auto",
        objectFit: "contain",
        opacity,
        display: "block",
        marginBottom,
        pointerEvents: "none",
        userSelect: "none",
      }}
    />
  );
}

function FlashOverlay({ visible }) {
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 20, pointerEvents: "none" }}>
      <div style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        height: "50%",
        background: "#000",
        opacity: 1,
        transform: visible ? "translateY(0%)" : "translateY(-130%)",
        transition: "transform 0.28s cubic-bezier(0.2, 0.85, 0.22, 1)",
      }} />
      <div style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "50%",
        background: "#000",
        opacity: 1,
        transform: visible ? "translateY(0%)" : "translateY(130%)",
        transition: "transform 0.28s cubic-bezier(0.2, 0.85, 0.22, 1)",
      }} />
    </div>
  );
}

function FrameGuideOverlay({ isLandscape }) {
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 7, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div
        style={{
          width: isLandscape ? "100%" : "auto",
          height: isLandscape ? "auto" : "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          aspectRatio: isLandscape ? "3 / 2" : "2 / 3",
          border: "1px solid rgba(255,107,0,0.38)",
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.22)",
        }}
      />
    </div>
  );
}

function LiveStamp({ blockHeight, style }) {
  // Fixed size so stamp looks identical in portrait/landscape.
  const fontSize = "14px";
  return (
    <div style={{
      position: "absolute", bottom: "5%", right: "4%", zIndex: 10,
      fontFamily: "'Share Tech Mono', 'DS-Digital', 'Digital-7 Mono', 'Courier New', monospace",
      fontWeight: "bold",
      fontSize,
      color: style.stampColor,
      textShadow: `0 0 8px ${style.stampGlow}, 0 0 20px ${style.stampGlow}, 0 0 2px ${style.stampColor}`,
      letterSpacing: "0.05em",
      userSelect: "none",
    }}>
      {Number.isFinite(blockHeight) ? `BLOCK ${blockHeight}` : "BLOCK ..."}
    </div>
  );
}

function ShotCounter({ shots, styleName }) {
  if (shots === Infinity || shots === undefined) return null;
  return (
    <div style={{ position: "absolute", top: "4%", right: "4%", zIndex: 10, textAlign: "right" }}>
      <div style={{ fontFamily: "'Courier New', monospace", fontSize: "10px", color: "#FF6B00", letterSpacing: "0.12em", marginBottom: 4, opacity: 0.8 }}>
        {styleName.toUpperCase()}
      </div>
      <div style={{ fontFamily: "'Courier New', monospace", fontSize: "18px", color: "#FF6B00", textShadow: "0 0 8px #FF4500", fontWeight: "bold" }}>
        {shots} ◉
      </div>
      <div style={{ display: "flex", gap: 2, marginTop: 4, justifyContent: "flex-end", flexWrap: "wrap", maxWidth: 80 }}>
        {Array.from({ length: Math.min(36, shots) }).map((_, i) => (
          <div key={i} style={{
            width: 4, height: 4, borderRadius: "50%",
            background: i < shots ? "#FF6B00" : "#333",
            boxShadow: i < shots ? "0 0 4px #FF4500" : "none",
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── QR Code renderer ──────────────────────────────────────────────────────────

function LightningQR({ invoice, amount, styleName, status, error, onCancel, onRetry }) {
  const [copied, setCopied] = useState(false);
  const isLoading = status === "loading";
  const isAwaitingPayment = status === "awaiting_payment";
  const isError = status === "error";

  const handleCopy = () => {
    if (!invoice) return;
    navigator.clipboard?.writeText(invoice).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 110,
      background: "rgba(0,0,0,0.97)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'Courier New', monospace",
      padding: 24,
    }}>
      <div style={{ fontSize: 10, color: "#FF6B00", letterSpacing: "0.2em", marginBottom: 8 }}>
        {isLoading ? "CREATING INVOICE" : isError ? "INVOICE ERROR" : "LIGHTNING INVOICE"}
      </div>
      <div style={{ fontSize: 28, color: "#fff", fontWeight: "bold", marginBottom: 4 }}>
        ⚡ {amount} sats
      </div>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 28, letterSpacing: "0.1em" }}>
        {styleName.toUpperCase()} ROLL
      </div>

      {isAwaitingPayment && (
        <div style={{
          width: 324, height: 324,
          background: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 24,
          padding: 12,
          position: "relative",
        }}>
          <a
            href={`lightning:${invoice}`}
            style={{ display: "inline-flex", cursor: "pointer" }}
            aria-label="Open invoice in lightning wallet"
          >
            <QRCode value={invoice} size={300} bgColor="#FFFFFF" fgColor="#000000" />
          </a>
        </div>
      )}

      {isLoading && (
        <div style={{ color: "#666", fontSize: 11, marginBottom: 24, letterSpacing: "0.08em" }}>
          CONTACTING NWC WALLET...
        </div>
      )}

      {isError && (
        <div style={{ color: "#FF4500", fontSize: 10, marginBottom: 24, letterSpacing: "0.08em", textAlign: "center", maxWidth: 320 }}>
          {error || "FAILED TO CREATE INVOICE"}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 324 }}>
        {isAwaitingPayment && (
          <div style={{ position: "relative", width: "100%" }}>
            <input
              readOnly
              value={invoice}
              onClick={(e) => e.currentTarget.select()}
              aria-label="Lightning invoice"
              style={{
                width: "100%",
                background: "#0a0a0a",
                border: "1px solid #2a2a2a",
                color: "#bbb",
                padding: "10px 42px 10px 10px",
                fontSize: 9,
                fontFamily: "'Courier New', monospace",
                letterSpacing: "0.02em",
              }}
            />
            <button
              onClick={handleCopy}
              aria-label="Copy invoice"
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                width: 28,
                height: 28,
                border: "none",
                background: "transparent",
                color: copied ? "#4CAF50" : "#FF6B00",
                fontSize: 15,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              {copied ? "✓" : "⧉"}
            </button>
          </div>
        )}
        {isError && (
          <button onClick={onRetry} style={{
            background: "#FF6B00", border: "none", color: "#000",
            padding: "13px", fontSize: 12, fontWeight: "bold",
            cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.12em",
            width: "100%",
          }}>
            RETRY
          </button>
        )}
        <button onClick={onCancel} style={{
          background: "none", border: "1px solid #333", color: "#555",
          padding: "11px", fontSize: 11, cursor: "pointer",
          fontFamily: "inherit", letterSpacing: "0.1em",
          width: "100%",
        }}>
          CANCEL
        </button>
      </div>

      <div style={{ marginTop: 20, fontSize: 9, color: "#333", letterSpacing: "0.1em", textAlign: "center" }}>
        EXPIRES IN 10 MINUTES · PAY FROM ANY LIGHTNING WALLET
      </div>
    </div>
  );
}

// ─── Film Roll Modal ──────────────────────────────────────────────────────────

function FilmRollModal({ onClose, inventory, activeStyleId, onBuy, onSelectStyle, isStandalone }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(0,0,0,0.95)",
      display: "flex", flexDirection: "column",
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{
        padding: isStandalone ? "calc(12px + env(safe-area-inset-top)) 20px 12px" : "24px 20px 12px",
        borderBottom: "1px solid #1a1a1a",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "#FF6B00", letterSpacing: "0.2em", marginBottom: 4 }}>BLOCKAMATIC</div>
          <div style={{ fontSize: 20, color: "#fff", fontWeight: "bold", letterSpacing: "0.05em" }}>FILM ROLLS</div>
        </div>
        <button onClick={onClose} style={{
          background: "none", border: "1px solid #222", color: "#666",
          padding: "8px 14px", cursor: "pointer",
          fontFamily: "inherit", fontSize: 11, letterSpacing: "0.1em",
        }}>CLOSE</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {Object.values(FILM_STYLES).map(style => {
          const shotsLeft = inventory[style.id];
          const owned     = shotsLeft === Infinity ? "∞" : (shotsLeft || 0);
          const isActive  = activeStyleId === style.id;
          const hasShots  = style.free || (shotsLeft > 0);

          return (
            <div
              key={style.id}
              onClick={() => {
                if (hasShots) onSelectStyle?.(style.id);
                else onBuy(style.id);
              }}
              style={{
                border: `1px solid ${isActive ? "#FF6B00" : "#1e1e1e"}`,
                padding: "16px",
                marginBottom: 12,
                background: isActive ? "rgba(255,107,0,0.04)" : "#080808",
                position: "relative",
                cursor: hasShots ? "pointer" : "default",
              }}
            >
              {isActive && (
                <div style={{ position: "absolute", top: 10, right: 10, fontSize: 9, color: "#FF6B00", letterSpacing: "0.15em" }}>
                  LOADED
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 14, color: "#fff", fontWeight: "bold", letterSpacing: "0.05em" }}>{style.name}</div>
                  <div style={{ fontSize: 9, color: "#444", letterSpacing: "0.1em", marginTop: 2 }}>
                    {style.iso} · {style.shots === Infinity ? "UNLIMITED" : `${style.shots} EXP`}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {style.free
                    ? <div style={{ fontSize: 11, color: "#4CAF50", letterSpacing: "0.1em" }}>FREE</div>
                    : <div style={{ fontSize: 13, color: "#FF6B00", fontWeight: "bold" }}>⚡ {style.price}</div>
                  }
                </div>
              </div>

              <div style={{ fontSize: 10, color: "#444", marginBottom: 12, lineHeight: 1.6 }}>
                {style.description}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 10, color: "#333", letterSpacing: "0.08em" }}>
                  IN BAG:{" "}
                  <span style={{ color: hasShots ? "#FF6B00" : "#333" }}>{owned} shots</span>
                </div>
                {!style.free && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onBuy(style.id);
                    }}
                    style={{
                      background: "none",
                      border: "1px solid #FF6B00",
                      color: "#FF6B00",
                      padding: "6px 14px",
                      fontSize: 10,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      letterSpacing: "0.1em",
                    }}
                  >
                    BUY ROLL
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: "10px 20px", borderTop: "1px solid #0e0e0e", fontSize: 9, color: "#2a2a2a", letterSpacing: "0.1em", textAlign: "center" }}>
        PAYMENTS PROCESSED VIA LIGHTNING · NOSTR WALLET CONNECT
      </div>
    </div>
  );
}

// ─── Gallery Modal ────────────────────────────────────────────────────────────

function GalleryModal({ photos, onClose, onDeletePhoto, isStandalone }) {
  const [selected, setSelected] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    moved: false,
    startIdx: null,
    mode: "add",
  });

  const handleDownload = async (photo, idx) => {
    if (!photo) return;
    const filename = `blockamatic_${idx + 1}.jpg`;
    const exportDataUrl = photo.dataUrl;

    // iOS Safari often ignores `a.download` for data URLs.
    // Using the Share Sheet gives iOS the "Save to Photos" option.
    try {
      if (navigator.share && navigator.canShare) {
        const res = await fetch(exportDataUrl);
        const blob = await res.blob();
        const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Blockamatic" });
          return;
        }
      }
    } catch {
      // Fall back to legacy download/open behavior.
    }

    const a = document.createElement("a");
    a.href = exportDataUrl;
    a.download = filename;
    a.rel = "noopener";
    a.click();
  };

  const handleDelete = () => {
    if (selected === null) return;
    onDeletePhoto?.(selected);
    setSelected(null);
  };

  const toggleSelection = (idx) => {
    setSelectedIndexes((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const addSelection = (idx) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= photos.length) return;
    setSelectedIndexes((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
  };

  const removeSelection = (idx) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= photos.length) return;
    setSelectedIndexes((prev) => prev.filter((i) => i !== idx));
  };

  const getTileIndexFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const tile = el?.closest?.("[data-gallery-index]");
    if (!tile) return null;
    const idx = Number(tile.getAttribute("data-gallery-index"));
    return Number.isInteger(idx) ? idx : null;
  };

  const handleSelectPointerDown = (e) => {
    if (!selectionMode) return;
    const idx = getTileIndexFromPoint(e.clientX, e.clientY);
    if (idx === null) return;

    dragStateRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      startIdx: idx,
      mode: selectedIndexes.includes(idx) ? "remove" : "add",
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleSelectPointerMove = (e) => {
    if (!selectionMode) return;
    const drag = dragStateRef.current;
    if (!drag.active || drag.pointerId !== e.pointerId) return;

    const dx = Math.abs(e.clientX - drag.startX);
    const dy = Math.abs(e.clientY - drag.startY);
    if (!drag.moved && Math.max(dx, dy) >= 8) {
      drag.moved = true;
      if (drag.mode === "remove") removeSelection(drag.startIdx);
      else addSelection(drag.startIdx);
    }

    if (!drag.moved) return;
    const idx = getTileIndexFromPoint(e.clientX, e.clientY);
    if (idx !== null) {
      if (drag.mode === "remove") removeSelection(idx);
      else addSelection(idx);
    }
  };

  const handleSelectPointerEnd = (e) => {
    if (!selectionMode) return;
    const drag = dragStateRef.current;
    if (!drag.active || drag.pointerId !== e.pointerId) return;

    e.currentTarget.releasePointerCapture?.(e.pointerId);

    if (drag.moved) {
      const idx = getTileIndexFromPoint(e.clientX, e.clientY);
      if (idx !== null) {
        if (drag.mode === "remove") removeSelection(idx);
        else addSelection(idx);
      }
    } else if (drag.startIdx !== null) {
      toggleSelection(drag.startIdx);
    }

    dragStateRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      moved: false,
      startIdx: null,
      mode: "add",
    };
  };

  const handleBulkDelete = () => {
    if (selectedIndexes.length === 0) return;
    const sorted = [...selectedIndexes].sort((a, b) => b - a);
    sorted.forEach((idx) => onDeletePhoto?.(idx));
    setSelectedIndexes([]);
    setSelectionMode(false);
  };

  const showPrevPhoto = () => {
    setSelected(prev => {
      if (prev === null || photos.length === 0) return prev;
      return prev > 0 ? prev - 1 : prev;
    });
  };

  const showNextPhoto = () => {
    setSelected(prev => {
      if (prev === null || photos.length === 0) return prev;
      return prev < photos.length - 1 ? prev + 1 : prev;
    });
  };

  const handleTouchStart = e => {
    setTouchStartX(e.changedTouches[0]?.clientX ?? null);
  };

  const handleTouchEnd = e => {
    if (touchStartX === null) return;
    const endX = e.changedTouches[0]?.clientX;
    if (typeof endX !== "number") return;

    const deltaX = endX - touchStartX;
    const swipeThreshold = 40;

    if (deltaX <= -swipeThreshold) showNextPhoto();
    if (deltaX >= swipeThreshold) showPrevPhoto();
    setTouchStartX(null);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "#000",
      display: "flex", flexDirection: "column",
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{
        padding: isStandalone ? "calc(12px + env(safe-area-inset-top)) 20px 20px" : "20px",
        borderBottom: "1px solid #111",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 9, color: "#FF6B00", letterSpacing: "0.2em" }}>BLOCKAMATIC</div>
          <div style={{ fontSize: 18, color: "#fff", fontWeight: "bold" }}>
            GALLERY <span style={{ color: "#333", fontSize: 12 }}>({photos.length})</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {selected === null && (
            <>
              <button
                onClick={() => {
                  setSelectionMode((prev) => !prev);
                  setSelectedIndexes([]);
                }}
                style={{
                  background: "none",
                  border: "1px solid #1a1a1a",
                  color: selectionMode ? "#FF6B00" : "#777",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 9,
                  letterSpacing: "0.1em",
                }}
              >
                {selectionMode ? "CANCEL" : "SELECT"}
              </button>
              {selectionMode && (
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedIndexes.length === 0}
                  style={{
                    background: "none",
                    border: "1px solid #FF4500",
                    color: selectedIndexes.length === 0 ? "#3a1d1d" : "#FF4500",
                    padding: "8px 10px",
                    cursor: selectedIndexes.length === 0 ? "default" : "pointer",
                    fontFamily: "inherit",
                    fontSize: 9,
                    letterSpacing: "0.1em",
                  }}
                >
                  DELETE {selectedIndexes.length > 0 ? `(${selectedIndexes.length})` : ""}
                </button>
              )}
            </>
          )}
          <button onClick={onClose} style={{
            background: "none", border: "1px solid #1a1a1a", color: "#555",
            padding: "8px 14px", cursor: "pointer",
            fontFamily: "inherit", fontSize: 10, letterSpacing: "0.1em",
          }}>CLOSE</button>
        </div>
      </div>

      {selected !== null ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: 16, overflow: "hidden" }}>
          <div
            style={{ flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={photos[selected].dataUrl}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </div>
          <div style={{ fontSize: 10, color: "#FF6B00", marginTop: 12, letterSpacing: "0.1em" }}>
            BLOCK {photos[selected].blockHeight} · {photos[selected].styleName.toUpperCase()}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 12, paddingBottom: "env(safe-area-inset-bottom)" }}>
            <button
              onClick={() => handleDownload(photos[selected], selected)}
              style={{
                background: "#FF6B00", border: "none", color: "#000",
                padding: "10px 20px", fontSize: 11, fontWeight: "bold",
                cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.12em",
              }}
            >
              SAVE
            </button>
            <button
              onClick={handleDelete}
              style={{
                background: "none",
                border: "1px solid #FF4500",
                color: "#FF4500",
                padding: "10px 20px",
                fontSize: 10,
                cursor: "pointer",
                fontFamily: "inherit",
                letterSpacing: "0.1em",
              }}
            >
              DELETE
            </button>
            <button
              onClick={() => setSelected(null)}
              style={{
                background: "none", border: "1px solid #222", color: "#666",
                padding: "10px 20px", fontSize: 10,
                cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.1em",
              }}
            >
              BACK
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          flex: 1, overflowY: "auto", padding: 12,
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 0,
          alignContent: "start",
          alignItems: "start",
        }}
        onPointerDown={handleSelectPointerDown}
        onPointerMove={handleSelectPointerMove}
        onPointerUp={handleSelectPointerEnd}
        onPointerCancel={handleSelectPointerEnd}
        >
          {photos.length === 0 && (
            <div style={{ gridColumn: "1/-1", color: "#2a2a2a", fontSize: 11, textAlign: "center", marginTop: 60, letterSpacing: "0.1em" }}>
              NO SHOTS TAKEN YET
            </div>
          )}
          {photos.map((p, i) => (
            <div
              key={i}
              data-gallery-index={i}
              onClick={() => {
                if (!selectionMode) setSelected(i);
              }}
              style={{
                position: "relative", cursor: "pointer",
                aspectRatio: "1/1", background: "#0a0a0a",
                overflow: "hidden",
                border: "none",
                outline: "1px solid #111",
                outlineOffset: "-1px",
                touchAction: selectionMode ? "none" : "auto",
              }}
            >
              <img src={p.dataUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {selectionMode && (
                <div style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "1px solid #FF6B00",
                  background: selectedIndexes.includes(i) ? "#FF6B00" : "rgba(0,0,0,0.6)",
                  color: "#000",
                  fontSize: 9,
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  {selectedIndexes.includes(i) ? "✓" : ""}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Style Picker Sheet ───────────────────────────────────────────────────────

function StylePickerSheet({ inventory, activeStyleId, onSelect, onBuyNeeded, onClose }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "flex-end" }}
      onClick={onClose}
    >
      <div
        style={{ width: "100%", background: "#080808", borderTop: "1px solid #1a1a1a", padding: "20px 16px 36px" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 9, color: "#FF6B00", letterSpacing: "0.2em", marginBottom: 14, textAlign: "center" }}>
          SELECT FILM
        </div>
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
          {Object.values(FILM_STYLES).map(style => {
            const shotsLeft = inventory[style.id];
            const locked    = !style.free && !(shotsLeft > 0);
            const isActive  = activeStyleId === style.id;

            return (
              <div
                key={style.id}
                onClick={() => locked ? onBuyNeeded(style.id) : onSelect(style.id)}
                style={{
                  minWidth: 88,
                  border: `1px solid ${isActive ? "#FF6B00" : locked ? "#161616" : "#2a2a2a"}`,
                  padding: "12px 10px",
                  cursor: "pointer",
                  background: isActive ? "rgba(255,107,0,0.06)" : "#000",
                  textAlign: "center",
                  flexShrink: 0,
                  transition: "border-color 0.15s",
                }}
              >
                <div style={{ fontSize: 16, marginBottom: 6 }}>
                  {locked
                    ? <span style={{ opacity: 0.3 }}>🔒</span>
                    : <span style={{ color: style.stampColor, textShadow: `0 0 8px ${style.stampGlow}` }}>◉</span>
                  }
                </div>
                <div style={{ fontSize: 9, color: locked ? "#2a2a2a" : "#bbb", fontWeight: "bold", letterSpacing: "0.06em", marginBottom: 3 }}>
                  {style.name.toUpperCase()}
                </div>
                <div style={{ fontSize: 8, color: locked ? "#1e1e1e" : "#444", letterSpacing: "0.05em" }}>
                  {style.free ? "FREE" : locked
                    ? `⚡${style.price}`
                    : `${shotsLeft === Infinity ? "∞" : shotsLeft} LEFT`
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AppInfoModal({ version, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 130,
        background: "rgba(0,0,0,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "'Courier New', monospace",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 360,
          background: "#060606",
          border: "1px solid #1e1e1e",
          padding: "20px 18px",
          textAlign: "center",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
          <BrandLogo src="/logo.png" height={21} opacity={0.95} />
        </div>
        <div style={{ fontSize: 14, color: "#fff", letterSpacing: "0.06em", marginBottom: 14 }}>
          APP VERSION
        </div>
        <div style={{ fontSize: 24, color: "#FF6B00", textShadow: "0 0 10px #FF4500", marginBottom: 18 }}>
          v{version}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "1px solid #333",
            color: "#777",
            padding: "8px 18px",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 10,
            letterSpacing: "0.12em",
          }}
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function Blockamatic() {
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const blockCache  = useRef({ height: null, ts: 0 });

  // ── State ────────────────────────────────────────────────────────────────────
  const [blockHeight,     setBlockHeight]     = useState(null);
  const [activeStyleId,   setActiveStyleId]   = useState("standard");
  const [inventory,       setInventory]       = useState({ standard: Infinity, kodachrome: 0, trix: 0, cinestill: 0, lomochrome: 0 });
  const [photos,          setPhotos]          = useState([]);
  const [cameraReady,     setCameraReady]     = useState(false);
  const [cameraError,     setCameraError]     = useState(null);
  const [showFilmModal,   setShowFilmModal]   = useState(false);
  const [showGallery,     setShowGallery]     = useState(false);
  const [showStylePicker, setShowStylePicker] = useState(false);
  const [showAppInfo,     setShowAppInfo]     = useState(false);
  const [isLandscape,     setIsLandscape]     = useState(() =>
    window.matchMedia("(orientation: landscape)").matches
  );
  const [isStandalone,   setIsStandalone]    = useState(() => {
    const mql = window.matchMedia?.("(display-mode: standalone)");
    return Boolean(mql?.matches) || window.navigator?.standalone === true;
  });
  const [viewportHeight,  setViewportHeight]  = useState(() =>
    Math.round(window.visualViewport?.height || window.innerHeight)
  );

  const activeStyle = FILM_STYLES[activeStyleId];

  // ── Load inventory from IndexedDB on mount ────────────────────────────────────
  useEffect(() => {
    loadInventory().then(setInventory).catch(console.error);
  }, []);

  useEffect(() => {
    getAllPhotos()
      .then((loaded) => {
        // Keep in-memory photo order consistent with store sorting (newest first).
        setPhotos(loaded);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(orientation: landscape)");
    const onChange = () => setIsLandscape(mql.matches);

    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const mql = window.matchMedia?.("(display-mode: standalone)");
    if (!mql) return;

    const onChange = () => setIsStandalone(Boolean(mql.matches) || window.navigator?.standalone === true);
    onChange();

    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      const next = Math.round(window.visualViewport?.height || window.innerHeight);
      setViewportHeight(prev => (Math.abs(prev - next) > 1 ? next : prev));
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("orientationchange", updateViewportHeight);
    window.visualViewport?.addEventListener("resize", updateViewportHeight);

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
      window.visualViewport?.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  // ── Refresh inventory after a purchase ───────────────────────────────────────
  const refreshInventory = useCallback(() => {
    loadInventory().then(setInventory).catch(console.error);
  }, []);

  // ── Block height ──────────────────────────────────────────────────────────────
  const fetchBlock = useCallback(async () => {
    const now = Date.now();
    if (blockCache.current.height && now - blockCache.current.ts < 60_000) {
      setBlockHeight(blockCache.current.height);
      return;
    }
    try {
      const res = await fetch("/api/block-height", { cache: "no-store" });
      if (!res.ok) throw new Error("Block API failed");
      const data = await res.json();
      const height = Number.parseInt(String(data.height), 10);
      if (!Number.isFinite(height)) throw new Error("Invalid block height");
      blockCache.current = { height, ts: now };
      setBlockHeight(height);
    } catch {
      if (blockCache.current.height) setBlockHeight(blockCache.current.height);
    }
  }, []);

  useEffect(() => {
    fetchBlock();
    const id = setInterval(fetchBlock, 60_000);
    return () => clearInterval(id);
  }, [fetchBlock]);

  // ── Camera ────────────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject    = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch {
      setCameraError("Camera access denied. Please allow camera permission.");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, [startCamera]);

  useEffect(() => {
    prefetchShutterAudio();
  }, []);

  // ── useCapture hook ───────────────────────────────────────────────────────────
  const { capture, capturing, shutterLocked, flash, statusMsg } = useCapture({
    activeStyleId,
    activeStyle,
    blockHeight,
    isLandscape,
    onPhotoCaptured: (photo) => {
      const id = (globalThis.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const photoWithId = { ...photo, id };

      setPhotos(prev => [photoWithId, ...prev]);

      // Fire-and-forget persistence; UI updates immediately.
      savePhoto(photoWithId).catch(console.error);

      // Refresh inventory so shot counter updates immediately after capture
      refreshInventory();
    },
  });

  const handleShutter = useCallback(() => {
    if (!cameraReady || !videoRef.current) return;
    primeShutterAudio();
    capture(videoRef.current);
  }, [cameraReady, capture]);
  const shutterBusy = capturing || shutterLocked;

  // ── Lightning purchase hook ───────────────────────────────────────────────────
  const {
    state:          purchaseState,
    invoice,
    currentStyleId: purchasingStyleId,
    error:          purchaseError,
    purchase,
    cancel:         cancelPurchase,
  } = useLightningPurchase({
    onSuccess: (styleId, shots) => {
      // Refresh inventory from IndexedDB — token already saved by the hook
      refreshInventory();
      // Auto-load the purchased style
      setActiveStyleId(styleId);
      setShowFilmModal(false);
    },
  });

  // ── Style selection ───────────────────────────────────────────────────────────
  const handleStyleSelect = (id) => {
    setActiveStyleId(id);
    setShowStylePicker(false);
  };

  const handleBuyNeeded = (id) => {
    setShowStylePicker(false);
    setShowFilmModal(true);
  };

  const handleBuyRoll = (styleId) => {
    setShowFilmModal(false);
    purchase(styleId);
  };

  const shotsLeft = inventory[activeStyleId];

  // Use grid to keep the shutter button pinned in view during iOS PWA
  // orientation transitions (avoids `100vh` + `justifyContent:center` timing bugs).
  const viewfinderContainerStyle = isLandscape
    ? {
        position: "relative",
        width: "100%",
        height: "100%",
        maxWidth: 900,
        background: "#0a0a0a",
        overflow: "hidden",
      }
    : {
        position: "relative",
        width: "100%",
        height: "100%",
        maxWidth: 480,
        background: "#0a0a0a",
        overflow: "hidden",
      };

  const portraitBottomBase = isStandalone ? 42 : 20;

  const controlsBarStyle = isLandscape
    ? {
        width: 150,
        height: "100%",
        background: "#050505",
        borderLeft: "1px solid #0e0e0e",
        padding: "20px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexShrink: 0,
        boxSizing: "border-box",
      }
    : {
        width: "100%",
        maxWidth: 480,
        background: "#050505",
        borderTop: "1px solid #0e0e0e",
        // Safe-area inset-top doesn't apply to the bottom controls; keep it bottom-only
        padding: `16px 24px calc(${portraitBottomBase}px + env(safe-area-inset-bottom))`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexShrink: 0,
        boxSizing: "border-box",
      };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      width: "100%",
      height: `${viewportHeight}px`,
      background: "#000",
      display: "grid",
      gridTemplateColumns: isLandscape ? "minmax(0, 1fr) 150px" : "minmax(0, 1fr)",
      gridTemplateRows: isLandscape ? "1fr" : "minmax(0, 1fr) auto",
      justifyItems: "center",
      alignItems: "stretch",
      fontFamily: "'Courier New', monospace",
      userSelect: "none", overflow: "hidden",
      boxSizing: "border-box",
      gap: isLandscape ? 14 : 0,
      padding: isLandscape ? "24px 16px" : 0,
    }}>
      <style>{`
        @keyframes blockamatic-shutter-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes blockamatic-shutter-pulse {
          0%, 100% { transform: scale(0.86); opacity: 0.45; }
          50% { transform: scale(1); opacity: 0.85; }
        }
      `}</style>

      {/* ── Lightning invoice QR ── */}
      {(purchaseState === "awaiting_payment" || purchaseState === "loading" || purchaseState === "error") && purchasingStyleId && (
        <LightningQR
          invoice={invoice || ""}
          amount={FILM_STYLES[purchasingStyleId]?.price}
          styleName={FILM_STYLES[purchasingStyleId]?.name}
          status={purchaseState}
          error={purchaseError}
          onRetry={() => purchase(purchasingStyleId)}
          onCancel={cancelPurchase}
        />
      )}

      {/* ── Payment expired notice ── */}
      {purchaseState === "expired" && (
        <div style={{
          position: "fixed", top: "10%", left: "50%", transform: "translateX(-50%)",
          zIndex: 200,
          background: "#000", border: "1px solid #FF4500",
          color: "#FF4500", padding: "12px 24px",
          fontSize: 11, letterSpacing: "0.15em",
          fontFamily: "'Courier New', monospace",
        }}>
          INVOICE EXPIRED · TRY AGAIN
        </div>
      )}

      {/* ── Modals ── */}
      {showFilmModal && (
        <FilmRollModal
          onClose={() => setShowFilmModal(false)}
          inventory={inventory}
          activeStyleId={activeStyleId}
          onBuy={handleBuyRoll}
          isStandalone={isStandalone}
          onSelectStyle={(id) => {
            setActiveStyleId(id);
            setShowFilmModal(false);
          }}
        />
      )}
      {showGallery && (
        <GalleryModal
          photos={photos}
          onClose={() => setShowGallery(false)}
          onDeletePhoto={(idx) => {
            const photo = photos[idx];
            if (photo?.id) deletePhoto(photo.id).catch(console.error);
            setPhotos(prev => prev.filter((_, i) => i !== idx));
          }}
          isStandalone={isStandalone}
        />
      )}
      {showStylePicker && (
        <StylePickerSheet
          inventory={inventory}
          activeStyleId={activeStyleId}
          onSelect={handleStyleSelect}
          onBuyNeeded={handleBuyNeeded}
          onClose={() => setShowStylePicker(false)}
        />
      )}
      {showAppInfo && (
        <AppInfoModal version={packageJson.version} onClose={() => setShowAppInfo(false)} />
      )}

      {/* ── Viewfinder ── */}
      <div style={viewfinderContainerStyle}>
        <video
          ref={videoRef}
          autoPlay playsInline muted
          style={{
            width: "100%", height: "100%",
            objectFit: "cover",
            filter: activeStyle.filter,
            display: cameraError ? "none" : "block",
          }}
        />

        {cameraError && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            color: "#444", fontSize: 11, letterSpacing: "0.1em",
            textAlign: "center", padding: 24,
          }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📷</div>
            <div style={{ color: "#FF4500", marginBottom: 8 }}>NO CAMERA</div>
            <div style={{ color: "#2a2a2a", fontSize: 9 }}>{cameraError}</div>
          </div>
        )}

        <FrameGuideOverlay isLandscape={isLandscape} />
        <GrainOverlay intensity={activeStyle.grain * 1.5} />
        <VignetteOverlay strength={activeStyle.vignette} />
        <FlashOverlay visible={flash} />
        <LiveStamp blockHeight={blockHeight} style={activeStyle} isLandscape={isLandscape} />
        <ShotCounter shots={shotsLeft} styleName={activeStyle.name} />

        {/* App label */}
        <div style={{ position: "absolute", top: "4%", left: "4%", zIndex: 10 }}>
          <button
            onClick={() => setShowAppInfo(true)}
            style={{
              fontSize: 8,
              color: "#FF6B00",
              letterSpacing: "0.25em",
              opacity: 0.6,
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            BLOCKAMATIC
          </button>
        </div>

        {/* Status toast */}
        {statusMsg && (
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            zIndex: 15,
            background: "rgba(0,0,0,0.88)",
            border: "1px solid #FF4500",
            color: "#FF6B00",
            fontSize: 11, padding: "10px 20px",
            letterSpacing: "0.15em", whiteSpace: "nowrap",
          }}>
            {statusMsg}
          </div>
        )}

        {/* Viewfinder corners */}
        {[
          { pos: "topLeft",     style: { top: 12,    left: 12,    borderWidth: "1px 0 0 1px" } },
          { pos: "topRight",    style: { top: 12,    right: 12,   borderWidth: "1px 1px 0 0" } },
          { pos: "bottomLeft",  style: { bottom: 12, left: 12,    borderWidth: "0 0 1px 1px" } },
          { pos: "bottomRight", style: { bottom: 12, right: 12,   borderWidth: "0 1px 1px 0" } },
        ].map(({ pos, style: s }) => (
          <div key={pos} style={{
            position: "absolute", width: 20, height: 20, zIndex: 8,
            borderColor: "rgba(255,107,0,0.35)",
            borderStyle: "solid",
            ...s,
          }} />
        ))}
      </div>

      {/* ── Controls bar ── */}
      <div style={controlsBarStyle}>

        {/* Gallery thumbnail */}
        <button
          onClick={() => setShowGallery(true)}
          style={{
            background: "none", border: "1px solid #1a1a1a",
            width: 48, height: 48,
            cursor: "pointer", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", flexShrink: 0,
            padding: 0,
          }}
        >
          {photos[0]
            ? <img
                src={photos[0].dataUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  // Bias crop away from bottom-right where the block stamp lives.
                  objectPosition: "center 30%",
                }}
              />
            : <span style={{ fontSize: 18, opacity: 0.3 }}>🗂</span>
          }
        </button>

        {/* Shutter */}
        <button
          onPointerDown={() => {
            if (!shutterBusy && cameraReady) primeShutterAudio();
          }}
          onClick={handleShutter}
          disabled={shutterBusy || !cameraReady}
          style={{
            width: 72, height: 72,
            borderRadius: "50%",
            border: `3px solid ${shutterBusy ? "#FF4500" : "#FF6B00"}`,
            background: "transparent",
            cursor: shutterBusy ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 20px rgba(255,107,0,0.25)",
            transition: "border-color 0.1s",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: shutterBusy ? "#FF4500" : "#FF6B00",
            opacity: shutterBusy ? 0.3 : 1,
            transition: "all 0.1s",
            animation: shutterBusy ? "blockamatic-shutter-pulse 0.75s ease-in-out infinite" : "none",
          }} />
          {shutterBusy && (
            <>
              <div style={{
                position: "absolute",
                inset: 6,
                borderRadius: "50%",
                border: "3px solid rgba(255,107,0,0.25)",
                borderTopColor: "#FF6B00",
                borderRightColor: "#FF4500",
                animation: "blockamatic-shutter-spin 0.65s linear infinite",
              }} />
              <div style={{
                position: "absolute",
                bottom: -16,
                fontSize: 8,
                letterSpacing: "0.14em",
                color: "#FF6B00",
                opacity: 0.9,
                whiteSpace: "nowrap",
              }}>
                WAIT
              </div>
            </>
          )}
        </button>

        {/* Film controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <button
            onClick={() => setShowStylePicker(s => !s)}
            style={{
              background: "none", border: "1px solid #1e1e1e",
              color: "#666", width: 52, height: 22,
              cursor: "pointer", fontSize: 8,
              letterSpacing: "0.12em", fontFamily: "inherit",
            }}
          >
            FILM
          </button>
          <button
            onClick={() => setShowFilmModal(true)}
            style={{
              background: "none", border: "1px solid #1e1e1e",
              color: "#FF6B00", width: 52, height: 22,
              cursor: "pointer", fontSize: 8,
              letterSpacing: "0.08em", fontFamily: "inherit",
            }}
          >
            ⚡ BUY
          </button>
        </div>
      </div>

    </div>
  );
}